/**
 * WebGL2 immediate-mode 2D renderer, the browser counterpart of FTL's CSurface
 * (GL_BlitImage, GL_DrawRect, GL_DrawLine, GL_Translate/GL_Rotate, GL_SetColorTint...).
 *
 * - Everything is drawn in FTL's logical 1280x720 space (y down). The frame is rendered
 *   into an offscreen target at an integer multiple k = ceil(scale) of 1280x720 (nearest
 *   neighbour, crisp), then scaled with bilinear filtering into a letterboxed rect of the
 *   canvas, so pixel art stays sharp at any window size and devicePixelRatio.
 * - One dynamic vertex buffer. Up to 8 textures are bound per batch (per-vertex slot
 *   index), so a batch only breaks when a 9th texture is needed or the clip rect changes.
 *   Untextured primitives and additive sprites never break a batch.
 * - Premultiplied alpha throughout: textures are uploaded premultiplied, vertex colors are
 *   premultiplied, blending is (ONE, ONE_MINUS_SRC_ALPHA). Additive blending uses the
 *   premultiplied trick (vertex alpha 0 => dst + src).
 */

export const LOGICAL_W = 1280;
export const LOGICAL_H = 720;

/** Straight (non-premultiplied) RGBA, each channel 0..1. */
export interface Color {
  r: number;
  g: number;
  b: number;
  a: number;
}

/** Color from 0..255 channels and 0..1 alpha. */
export function rgba(r: number, g: number, b: number, a = 1): Color {
  return { r: r / 255, g: g / 255, b: b / 255, a };
}

export const WHITE: Color = Object.freeze({ r: 1, g: 1, b: 1, a: 1 });
export const BLACK: Color = Object.freeze({ r: 0, g: 0, b: 0, a: 1 });

export interface Texture {
  /** Pixel size (0 until loaded). */
  w: number;
  h: number;
  glTex: WebGLTexture | null;
  /** True once real pixels are uploaded (or the missing-file fallback is in place). */
  ready: boolean;
  /** Load failed; `glTex` is the missing-texture checker. */
  failed: boolean;
  /** Bilinear sampling (default nearest, FTL art is pixel art). */
  linear: boolean;
  /** Single-channel coverage texture (fonts): sampled as premultiplied white * coverage. */
  alphaOnly: boolean;
  /** Source path, for debugging. */
  path?: string;
}

export interface BlitOptions {
  /** Multiplied into the texel (after the color stack). */
  color?: Color;
  /** Extra alpha multiplier. */
  alpha?: number;
  /** Degrees; positive is clockwise on screen (same as FTL's GL_Rotate in y-down space). */
  rotation?: number;
  /** Rotation pivot relative to the destination rect's top-left. Default: its center. */
  pivotX?: number;
  pivotY?: number;
  flipX?: boolean;
  flipY?: boolean;
  additive?: boolean;
}

export interface RendererOptions {
  logicalWidth?: number;
  logicalHeight?: number;
  /** Largest integer supersample factor for the offscreen target (default 4). */
  maxSupersample?: number;
}

export interface FrameStats {
  drawCalls: number;
  vertices: number;
}

const WORDS_PER_VERTEX = 6; // x, y, u, v (f32) | rgba8 | slot (u8) + pad
const BYTES_PER_VERTEX = WORDS_PER_VERTEX * 4;
const MAX_VERTICES = 6 * 16384;
const SLOTS = 8;
/** Slot code: bits 0-2 texture unit, bit 3 alpha-only; UNTEXTURED = no sampling. */
const ALPHA_ONLY_BIT = 8;
const UNTEXTURED = 255;

const VS = `#version 300 es
layout(location = 0) in vec2 a_pos;
layout(location = 1) in vec2 a_uv;
layout(location = 2) in vec4 a_color;
layout(location = 3) in uint a_tex;
uniform vec2 u_scale;
out highp vec2 v_uv;
out vec4 v_color;
flat out uint v_tex;
void main() {
  v_uv = a_uv;
  v_color = a_color;
  v_tex = a_tex;
  gl_Position = vec4(a_pos * u_scale + vec2(-1.0, 1.0), 0.0, 1.0);
}`;

const FS = `#version 300 es
precision mediump float;
uniform sampler2D u_tex[${SLOTS}];
in highp vec2 v_uv;
in vec4 v_color;
flat in uint v_tex;
out vec4 o_color;
void main() {
  if (v_tex == ${UNTEXTURED}u) { o_color = v_color; return; }
  uint unit = v_tex & 7u;
  vec4 t;
  ${Array.from({ length: SLOTS }, (_, i) => `${i ? 'else ' : ''}if (unit == ${i}u) t = texture(u_tex[${i}], v_uv);`).join('\n  ')}
  if ((v_tex & ${ALPHA_ONLY_BIT}u) != 0u) t = vec4(t.r);
  o_color = t * v_color;
}`;

interface Clip {
  x: number;
  y: number;
  w: number;
  h: number;
}

export class Renderer {
  readonly canvas: HTMLCanvasElement;
  readonly gl: WebGL2RenderingContext;
  readonly width: number;
  readonly height: number;
  /** Stats of the last completed frame. */
  readonly stats: FrameStats = { drawCalls: 0, vertices: 0 };

  private readonly program: WebGLProgram;
  private readonly uScale: WebGLUniformLocation;
  private readonly vao: WebGLVertexArrayObject;
  private readonly vbo: WebGLBuffer;
  private readonly data = new ArrayBuffer(MAX_VERTICES * BYTES_PER_VERTEX);
  private readonly f32 = new Float32Array(this.data);
  private readonly u32 = new Uint32Array(this.data);
  private count = 0;
  /** Textures bound to units 0..SLOTS-1 for the current batch. */
  private readonly slots: (Texture | null)[] = new Array<Texture | null>(SLOTS).fill(null);
  private readonly maxSupersample: number;
  private frame: FrameStats = { drawCalls: 0, vertices: 0 };
  private inFrame = false;

  // transform: x' = a*x + c*y + e ; y' = b*x + d*y + f
  private m = [1, 0, 0, 1, 0, 0];
  private readonly mStack: number[][] = [];
  // color tint stack (straight alpha)
  private tint: Color = { ...WHITE };
  private readonly tintStack: Color[] = [];
  private clip: Clip | null = null;
  private readonly clipStack: (Clip | null)[] = [];

  // offscreen target + letterbox
  private fbo: WebGLFramebuffer | null = null;
  private fboTex: WebGLTexture | null = null;
  private fboScale = 1;
  private fboW = 0;
  private fboH = 0;
  private view = { x: 0, y: 0, w: 1, h: 1, scale: 1 };
  private lastCss = { w: -1, h: -1, dpr: -1 };

  constructor(canvas: HTMLCanvasElement, opts: RendererOptions = {}) {
    this.canvas = canvas;
    this.width = opts.logicalWidth ?? LOGICAL_W;
    this.height = opts.logicalHeight ?? LOGICAL_H;
    this.maxSupersample = opts.maxSupersample ?? 4;
    const gl = canvas.getContext('webgl2', {
      alpha: false,
      antialias: false,
      depth: false,
      stencil: false,
      premultipliedAlpha: true,
      preserveDrawingBuffer: false,
    });
    if (!gl) throw new Error('WebGL2 is not available');
    this.gl = gl;

    this.program = linkProgram(gl, VS, FS);
    this.uScale = gl.getUniformLocation(this.program, 'u_scale')!;
    gl.useProgram(this.program);
    gl.uniform1iv(
      gl.getUniformLocation(this.program, 'u_tex'),
      Array.from({ length: SLOTS }, (_, i) => i),
    );

    this.vao = gl.createVertexArray()!;
    this.vbo = gl.createBuffer()!;
    gl.bindVertexArray(this.vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.vbo);
    gl.bufferData(gl.ARRAY_BUFFER, this.data.byteLength, gl.DYNAMIC_DRAW);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, BYTES_PER_VERTEX, 0);
    gl.enableVertexAttribArray(1);
    gl.vertexAttribPointer(1, 2, gl.FLOAT, false, BYTES_PER_VERTEX, 8);
    gl.enableVertexAttribArray(2);
    gl.vertexAttribPointer(2, 4, gl.UNSIGNED_BYTE, true, BYTES_PER_VERTEX, 16);
    gl.enableVertexAttribArray(3);
    gl.vertexAttribIPointer(3, 1, gl.UNSIGNED_BYTE, BYTES_PER_VERTEX, 20);

    gl.disable(gl.DEPTH_TEST);
    gl.disable(gl.CULL_FACE);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    this.resize();
  }

  // --- textures -----------------------------------------------------------------

  /** A texture object with no pixels yet (`ready` false); fill it with `upload*`. */
  createEmptyTexture(path?: string): Texture {
    return { w: 0, h: 0, glTex: null, ready: false, failed: false, linear: false, alphaOnly: false, path };
  }

  /** Uploads an ImageBitmap (decode with premultiplyAlpha: 'premultiply'), canvas or image. */
  uploadImage(tex: Texture, source: TexImageSource & { width: number; height: number }): Texture {
    const gl = this.gl;
    this.prepareUpload(tex);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, gl.RGBA, gl.UNSIGNED_BYTE, source);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
    return this.finishUpload(tex, source.width, source.height, false);
  }

  /** Uploads straight-alpha RGBA8 pixels (premultiplied on the CPU). */
  uploadRgba(tex: Texture, w: number, h: number, pixels: Uint8Array): Texture {
    const gl = this.gl;
    const pm = new Uint8Array(w * h * 4);
    for (let i = 0; i < pm.length; i += 4) {
      const a = pixels[i + 3]!;
      pm[i] = (pixels[i]! * a + 127) / 255;
      pm[i + 1] = (pixels[i + 1]! * a + 127) / 255;
      pm[i + 2] = (pixels[i + 2]! * a + 127) / 255;
      pm[i + 3] = a;
    }
    this.prepareUpload(tex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, w, h, 0, gl.RGBA, gl.UNSIGNED_BYTE, pm);
    return this.finishUpload(tex, w, h, false);
  }

  /** Uploads 8-bit coverage (font atlases); drawn as white * coverage * color. */
  uploadAlpha(tex: Texture, w: number, h: number, pixels: Uint8Array): Texture {
    const gl = this.gl;
    this.prepareUpload(tex);
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.R8, w, h, 0, gl.RED, gl.UNSIGNED_BYTE, pixels);
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 4);
    return this.finishUpload(tex, w, h, true);
  }

  createRgbaTexture(w: number, h: number, pixels: Uint8Array, linear = false): Texture {
    const tex = this.createEmptyTexture();
    tex.linear = linear;
    return this.uploadRgba(tex, w, h, pixels);
  }

  createAlphaTexture(w: number, h: number, pixels: Uint8Array, linear = false): Texture {
    const tex = this.createEmptyTexture();
    tex.linear = linear;
    return this.uploadAlpha(tex, w, h, pixels);
  }

  setTextureLinear(tex: Texture, linear: boolean): void {
    tex.linear = linear;
    if (!tex.glTex) return;
    this.flush();
    this.bindForSetup(tex.glTex);
    const gl = this.gl;
    const f = linear ? gl.LINEAR : gl.NEAREST;
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, f);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, f);
  }

  deleteTexture(tex: Texture): void {
    if (this.slots.includes(tex)) {
      this.flush();
      this.slots.fill(null);
    }
    if (tex.glTex) this.gl.deleteTexture(tex.glTex);
    tex.glTex = null;
    tex.ready = false;
  }

  /** Binds a texture on unit 0 for uploads/parameters; invalidates the batch slots. */
  private bindForSetup(glTex: WebGLTexture | null): void {
    const gl = this.gl;
    this.slots.fill(null);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, glTex);
  }

  private prepareUpload(tex: Texture): void {
    this.flush(); // pending vertices refer to the textures currently bound
    if (!tex.glTex) tex.glTex = this.gl.createTexture();
    this.bindForSetup(tex.glTex);
  }

  private finishUpload(tex: Texture, w: number, h: number, alphaOnly: boolean): Texture {
    const gl = this.gl;
    const f = tex.linear ? gl.LINEAR : gl.NEAREST;
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, f);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, f);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    tex.w = w;
    tex.h = h;
    tex.alphaOnly = alphaOnly;
    tex.ready = true;
    return tex;
  }

  // --- frame ------------------------------------------------------------------------

  /** Re-reads the canvas CSS size and devicePixelRatio; called automatically per frame. */
  resize(): void {
    if (this.inFrame) return; // applied at the next beginFrame
    const dpr = window.devicePixelRatio || 1;
    const cssW = this.canvas.clientWidth || this.width;
    const cssH = this.canvas.clientHeight || this.height;
    if (cssW === this.lastCss.w && cssH === this.lastCss.h && dpr === this.lastCss.dpr && this.fbo) return;
    this.lastCss = { w: cssW, h: cssH, dpr };
    const cw = Math.max(1, Math.round(cssW * dpr));
    const ch = Math.max(1, Math.round(cssH * dpr));
    if (this.canvas.width !== cw) this.canvas.width = cw;
    if (this.canvas.height !== ch) this.canvas.height = ch;

    const scale = Math.min(cw / this.width, ch / this.height);
    const vw = Math.round(this.width * scale);
    const vh = Math.round(this.height * scale);
    this.view = { x: Math.floor((cw - vw) / 2), y: Math.floor((ch - vh) / 2), w: vw, h: vh, scale };

    // Supersample at the next integer factor and minify: sharper than upscaling at 1.25-1.75x.
    const k = Math.max(1, Math.min(this.maxSupersample, Math.ceil(scale - 1e-6)));
    if (k !== this.fboScale || !this.fbo) this.createTarget(k);
  }

  private createTarget(k: number): void {
    const gl = this.gl;
    if (this.fbo) gl.deleteFramebuffer(this.fbo);
    if (this.fboTex) gl.deleteTexture(this.fboTex);
    this.fboScale = k;
    this.fboW = this.width * k;
    this.fboH = this.height * k;
    this.fboTex = gl.createTexture()!;
    this.bindForSetup(this.fboTex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, this.fboW, this.fboH, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    this.fbo = gl.createFramebuffer()!;
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.fbo);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, this.fboTex, 0);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  }

  /** Starts a frame: resets transform/color/clip stacks and clears to `clear` (default black). */
  beginFrame(clear: Color = BLACK): void {
    const gl = this.gl;
    this.resize();
    this.inFrame = true;
    this.frame = { drawCalls: 0, vertices: 0 };
    this.m = [1, 0, 0, 1, 0, 0];
    this.mStack.length = 0;
    this.tint = { ...WHITE };
    this.tintStack.length = 0;
    this.clip = null;
    this.clipStack.length = 0;
    this.count = 0;
    // Unbind the target's own texture (bound on unit 0 by the last present) so no sampler
    // references the framebuffer we draw into (WebGL rejects such feedback loops).
    this.bindForSetup(null);

    gl.bindFramebuffer(gl.FRAMEBUFFER, this.fbo);
    gl.viewport(0, 0, this.fboW, this.fboH);
    gl.disable(gl.SCISSOR_TEST);
    gl.clearColor(clear.r * clear.a, clear.g * clear.a, clear.b * clear.a, 1);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.useProgram(this.program);
    gl.bindVertexArray(this.vao);
    gl.uniform2f(this.uScale, 2 / this.width, -2 / this.height);
  }

  /** Flushes and presents the offscreen frame into the letterboxed canvas. */
  endFrame(): void {
    if (!this.inFrame) return;
    this.flush();
    this.inFrame = false;
    const gl = this.gl;
    this.stats.drawCalls = this.frame.drawCalls;
    this.stats.vertices = this.frame.vertices;

    gl.disable(gl.SCISSOR_TEST);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, this.canvas.width, this.canvas.height);
    gl.clearColor(0, 0, 0, 1);
    gl.clear(gl.COLOR_BUFFER_BIT);

    // Present: device-pixel quad; the FBO is bottom-up so v is flipped.
    const v = this.view;
    const exact = v.w === this.fboW && v.h === this.fboH;
    this.bindForSetup(this.fboTex);
    const f = exact ? gl.NEAREST : gl.LINEAR;
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, f);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, f);
    gl.uniform2f(this.uScale, 2 / this.canvas.width, -2 / this.canvas.height);
    const c = 0xffffffff;
    const x0 = v.x;
    const y0 = v.y;
    const x1 = v.x + v.w;
    const y1 = v.y + v.h;
    this.vert(x0, y0, 0, 1, c, 0);
    this.vert(x1, y0, 1, 1, c, 0);
    this.vert(x1, y1, 1, 0, c, 0);
    this.vert(x0, y0, 0, 1, c, 0);
    this.vert(x1, y1, 1, 0, c, 0);
    this.vert(x0, y1, 0, 0, c, 0);
    this.drawBuffered();
    this.slots.fill(null);
  }

  /** Maps a mouse/touch position (clientX/Y) to logical 1280x720 coordinates. */
  screenToLogical(clientX: number, clientY: number): { x: number; y: number } {
    const rect = this.canvas.getBoundingClientRect();
    const sx = this.canvas.width / (rect.width || 1);
    const sy = this.canvas.height / (rect.height || 1);
    const px = (clientX - rect.left) * sx;
    const py = (clientY - rect.top) * sy;
    return { x: (px - this.view.x) / this.view.scale, y: (py - this.view.y) / this.view.scale };
  }

  // --- state stacks -------------------------------------------------------------------

  push(): void {
    this.mStack.push(this.m.slice());
  }

  pop(): void {
    const m = this.mStack.pop();
    if (m) this.m = m;
  }

  resetTransform(): void {
    this.m = [1, 0, 0, 1, 0, 0];
  }

  translate(x: number, y: number): void {
    const m = this.m;
    m[4] += m[0]! * x + m[2]! * y;
    m[5] += m[1]! * x + m[3]! * y;
  }

  scale(sx: number, sy: number = sx): void {
    const m = this.m;
    m[0] *= sx;
    m[1] *= sx;
    m[2] *= sy;
    m[3] *= sy;
  }

  /** Degrees, clockwise on screen (GL_Rotate around z in FTL's y-down projection). */
  rotate(deg: number): void {
    const r = (deg * Math.PI) / 180;
    const cos = Math.cos(r);
    const sin = Math.sin(r);
    const [a, b, c, d] = this.m as [number, number, number, number];
    this.m[0] = a * cos + c * sin;
    this.m[1] = b * cos + d * sin;
    this.m[2] = c * cos - a * sin;
    this.m[3] = d * cos - b * sin;
  }

  /** Multiplies `c` into every following draw until popColor (FTL: GL_SetColorTint). */
  pushColor(c: Color): void {
    this.tintStack.push(this.tint);
    this.tint = { r: this.tint.r * c.r, g: this.tint.g * c.g, b: this.tint.b * c.b, a: this.tint.a * c.a };
  }

  popColor(): void {
    const t = this.tintStack.pop();
    if (t) this.tint = t;
  }

  /**
   * Restricts drawing to a rect given in the current coordinate space (translation and
   * scale are applied; rotation uses the bounding box). Intersects with the parent clip.
   */
  pushClip(x: number, y: number, w: number, h: number): void {
    this.clipStack.push(this.clip);
    const m = this.m;
    const cx = [x, x + w, x, x + w];
    const cy = [y, y, y + h, y + h];
    const xs = cx.map((px, i) => m[0]! * px + m[2]! * cy[i]! + m[4]!);
    const ys = cx.map((px, i) => m[1]! * px + m[3]! * cy[i]! + m[5]!);
    const x0 = Math.min(...xs);
    const y0 = Math.min(...ys);
    let c: Clip = { x: x0, y: y0, w: Math.max(...xs) - x0, h: Math.max(...ys) - y0 };
    if (this.clip) c = intersect(c, this.clip);
    this.setClip(c);
  }

  popClip(): void {
    this.setClip(this.clipStack.pop() ?? null);
  }

  private setClip(c: Clip | null): void {
    this.flush();
    this.clip = c;
    const gl = this.gl;
    if (!c) {
      gl.disable(gl.SCISSOR_TEST);
      return;
    }
    const k = this.fboScale;
    const x0 = Math.round(c.x * k);
    const x1 = Math.round((c.x + c.w) * k);
    const y0 = Math.round(c.y * k);
    const y1 = Math.round((c.y + c.h) * k);
    gl.enable(gl.SCISSOR_TEST);
    gl.scissor(x0, this.fboH - y1, Math.max(0, x1 - x0), Math.max(0, y1 - y0));
  }

  // --- sprites -----------------------------------------------------------------------

  /** Draws a whole texture at its natural size with its top-left at (x, y). */
  blit(tex: Texture, x: number, y: number, opts?: BlitOptions): void {
    if (!tex.ready) return;
    this.sprite(tex, x, y, tex.w, tex.h, 0, 0, 1, 1, opts);
  }

  blitScaled(tex: Texture, x: number, y: number, w: number, h: number, opts?: BlitOptions): void {
    if (!tex.ready) return;
    this.sprite(tex, x, y, w, h, 0, 0, 1, 1, opts);
  }

  /** Draws the source rect (texture pixels) into the destination rect. */
  blitRegion(
    tex: Texture,
    sx: number,
    sy: number,
    sw: number,
    sh: number,
    dx: number,
    dy: number,
    dw: number,
    dh: number,
    opts?: BlitOptions,
  ): void {
    if (!tex.ready || tex.w === 0) return;
    this.sprite(tex, dx, dy, dw, dh, sx / tex.w, sy / tex.h, (sx + sw) / tex.w, (sy + sh) / tex.h, opts);
  }

  /** Low-level textured quad with explicit UVs (0..1); used by text rendering. */
  quad(tex: Texture, x: number, y: number, w: number, h: number, u0: number, v0: number, u1: number, v1: number, color?: Color): void {
    if (!tex.ready) return;
    this.reserve(6);
    const slot = this.slotFor(tex);
    this.emitQuad(x, y, x + w, y, x + w, y + h, x, y + h, u0, v0, u1, v1, this.packColor(color, 1, false), slot);
  }

  private sprite(
    tex: Texture,
    x: number,
    y: number,
    w: number,
    h: number,
    u0: number,
    v0: number,
    u1: number,
    v1: number,
    opts?: BlitOptions,
  ): void {
    this.reserve(6);
    const slot = this.slotFor(tex);
    let ua = u0;
    let ub = u1;
    let va = v0;
    let vb = v1;
    if (opts?.flipX) [ua, ub] = [ub, ua];
    if (opts?.flipY) [va, vb] = [vb, va];
    const c = this.packColor(opts?.color, opts?.alpha ?? 1, opts?.additive ?? false);
    const rot = opts?.rotation ?? 0;
    if (rot === 0) {
      this.emitQuad(x, y, x + w, y, x + w, y + h, x, y + h, ua, va, ub, vb, c, slot);
      return;
    }
    const px = x + (opts?.pivotX ?? w / 2);
    const py = y + (opts?.pivotY ?? h / 2);
    const r = (rot * Math.PI) / 180;
    const cos = Math.cos(r);
    const sin = Math.sin(r);
    const rx = (lx: number, ly: number) => px + (lx - px) * cos - (ly - py) * sin;
    const ry = (lx: number, ly: number) => py + (lx - px) * sin + (ly - py) * cos;
    this.emitQuad(
      rx(x, y), ry(x, y),
      rx(x + w, y), ry(x + w, y),
      rx(x + w, y + h), ry(x + w, y + h),
      rx(x, y + h), ry(x, y + h),
      ua, va, ub, vb, c, slot,
    );
  }

  // --- primitives ------------------------------------------------------------------------

  rect(x: number, y: number, w: number, h: number, color: Color): void {
    this.reserve(6);
    this.emitQuad(x, y, x + w, y, x + w, y + h, x, y + h, 0, 0, 0, 0, this.packColor(color, 1, false), UNTEXTURED);
  }

  /** Outline drawn inside the rect (FTL: GL_DrawRectOutline). */
  rectOutline(x: number, y: number, w: number, h: number, color: Color, thickness = 1): void {
    const t = Math.min(thickness, w / 2, h / 2);
    this.rect(x, y, w, t, color);
    this.rect(x, y + h - t, w, t, color);
    this.rect(x, y + t, t, h - 2 * t, color);
    this.rect(x + w - t, y + t, t, h - 2 * t, color);
  }

  line(x1: number, y1: number, x2: number, y2: number, color: Color, thickness = 1): void {
    const dx = x2 - x1;
    const dy = y2 - y1;
    const len = Math.hypot(dx, dy);
    if (len === 0) return;
    const nx = (-dy / len) * (thickness / 2);
    const ny = (dx / len) * (thickness / 2);
    this.reserve(6);
    const c = this.packColor(color, 1, false);
    this.emitQuad(x1 + nx, y1 + ny, x2 + nx, y2 + ny, x2 - nx, y2 - ny, x1 - nx, y1 - ny, 0, 0, 0, 0, c, UNTEXTURED);
  }

  triangle(x1: number, y1: number, x2: number, y2: number, x3: number, y3: number, color: Color): void {
    const c = this.packColor(color, 1, false);
    this.reserve(3);
    this.vertT(x1, y1, c);
    this.vertT(x2, y2, c);
    this.vertT(x3, y3, c);
  }

  /** Filled convex polygon from a flat [x0, y0, x1, y1, ...] list (triangle fan). */
  polygon(points: ArrayLike<number>, color: Color): void {
    const n = points.length >> 1;
    if (n < 3) return;
    const c = this.packColor(color, 1, false);
    for (let i = 1; i < n - 1; i++) {
      this.reserve(3);
      this.vertT(points[0]!, points[1]!, c);
      this.vertT(points[i * 2]!, points[i * 2 + 1]!, c);
      this.vertT(points[i * 2 + 2]!, points[i * 2 + 3]!, c);
    }
  }

  circle(cx: number, cy: number, r: number, color: Color): void {
    this.ellipse(cx, cy, r, r, color);
  }

  circleOutline(cx: number, cy: number, r: number, color: Color, thickness = 1): void {
    this.ellipseOutline(cx, cy, r, r, color, thickness);
  }

  ellipse(cx: number, cy: number, rx: number, ry: number, color: Color): void {
    const c = this.packColor(color, 1, false);
    const n = segmentsFor(Math.max(rx, ry));
    let px = cx + rx;
    let py = cy;
    for (let i = 1; i <= n; i++) {
      const a = (i / n) * Math.PI * 2;
      const x = cx + Math.cos(a) * rx;
      const y = cy + Math.sin(a) * ry;
      this.reserve(3);
      this.vertT(cx, cy, c);
      this.vertT(px, py, c);
      this.vertT(x, y, c);
      px = x;
      py = y;
    }
  }

  /** Ring between the radii and the radii minus `thickness` (drawn inward). */
  ellipseOutline(cx: number, cy: number, rx: number, ry: number, color: Color, thickness = 1): void {
    const c = this.packColor(color, 1, false);
    const n = segmentsFor(Math.max(rx, ry));
    const irx = Math.max(0, rx - thickness);
    const iry = Math.max(0, ry - thickness);
    for (let i = 0; i < n; i++) {
      const a0 = (i / n) * Math.PI * 2;
      const a1 = ((i + 1) / n) * Math.PI * 2;
      const c0 = Math.cos(a0);
      const s0 = Math.sin(a0);
      const c1 = Math.cos(a1);
      const s1 = Math.sin(a1);
      this.reserve(6);
      this.emitQuad(
        cx + c0 * rx, cy + s0 * ry,
        cx + c1 * rx, cy + s1 * ry,
        cx + c1 * irx, cy + s1 * iry,
        cx + c0 * irx, cy + s0 * iry,
        0, 0, 0, 0, c, UNTEXTURED,
      );
    }
  }

  // --- batching internals ------------------------------------------------------------------

  /** Slot code for `tex` in the current batch; binds it to a free unit or starts a new batch. */
  private slotFor(tex: Texture): number {
    const slots = this.slots;
    let i = slots.indexOf(tex);
    if (i < 0) {
      i = slots.indexOf(null);
      if (i < 0) {
        this.flush();
        slots.fill(null);
        i = 0;
      }
      slots[i] = tex;
      const gl = this.gl;
      gl.activeTexture(gl.TEXTURE0 + i);
      gl.bindTexture(gl.TEXTURE_2D, tex.glTex);
    }
    return tex.alphaOnly ? i | ALPHA_ONLY_BIT : i;
  }

  /** Premultiplied RGBA8 packed little-endian; additive => alpha 0. */
  private packColor(color: Color | undefined, alpha: number, additive: boolean): number {
    const t = this.tint;
    const a = clamp01(t.a * (color ? color.a : 1) * alpha);
    const r = clamp01(t.r * (color ? color.r : 1)) * a;
    const g = clamp01(t.g * (color ? color.g : 1)) * a;
    const b = clamp01(t.b * (color ? color.b : 1)) * a;
    return (
      ((additive ? 0 : Math.round(a * 255)) << 24) |
      (Math.round(b * 255) << 16) |
      (Math.round(g * 255) << 8) |
      Math.round(r * 255)
    ) >>> 0;
  }

  /** Two triangles from 4 local-space corners (current transform applied). Call reserve(6) first. */
  private emitQuad(
    x0: number, y0: number,
    x1: number, y1: number,
    x2: number, y2: number,
    x3: number, y3: number,
    u0: number, v0: number, u1: number, v1: number,
    c: number,
    slot: number,
  ): void {
    const [a, b, cc, d, e, f] = this.m as [number, number, number, number, number, number];
    const X0 = a * x0 + cc * y0 + e, Y0 = b * x0 + d * y0 + f;
    const X1 = a * x1 + cc * y1 + e, Y1 = b * x1 + d * y1 + f;
    const X2 = a * x2 + cc * y2 + e, Y2 = b * x2 + d * y2 + f;
    const X3 = a * x3 + cc * y3 + e, Y3 = b * x3 + d * y3 + f;
    this.vert(X0, Y0, u0, v0, c, slot);
    this.vert(X1, Y1, u1, v0, c, slot);
    this.vert(X2, Y2, u1, v1, c, slot);
    this.vert(X0, Y0, u0, v0, c, slot);
    this.vert(X2, Y2, u1, v1, c, slot);
    this.vert(X3, Y3, u0, v1, c, slot);
  }

  /** Untextured vertex in local space (current transform applied). */
  private vertT(x: number, y: number, c: number): void {
    const m = this.m;
    this.vert(m[0]! * x + m[2]! * y + m[4]!, m[1]! * x + m[3]! * y + m[5]!, 0, 0, c, UNTEXTURED);
  }

  private vert(x: number, y: number, u: number, v: number, c: number, slot: number): void {
    const o = this.count * WORDS_PER_VERTEX;
    this.f32[o] = x;
    this.f32[o + 1] = y;
    this.f32[o + 2] = u;
    this.f32[o + 3] = v;
    this.u32[o + 4] = c;
    this.u32[o + 5] = slot;
    this.count++;
  }

  /** Makes room for n vertices; a flush keeps the bound slots (they stay valid). */
  private reserve(n: number): void {
    if (this.count + n > MAX_VERTICES) this.flush();
  }

  /** Submits pending vertices. Called automatically; public for callers mixing raw GL. */
  flush(): void {
    if (this.count === 0) return;
    this.drawBuffered();
  }

  private drawBuffered(): void {
    const gl = this.gl;
    gl.bindBuffer(gl.ARRAY_BUFFER, this.vbo);
    gl.bufferSubData(gl.ARRAY_BUFFER, 0, this.u32, 0, this.count * WORDS_PER_VERTEX);
    gl.drawArrays(gl.TRIANGLES, 0, this.count);
    this.frame.drawCalls++;
    this.frame.vertices += this.count;
    this.count = 0;
  }
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

function segmentsFor(r: number): number {
  return Math.max(12, Math.min(128, Math.ceil(r * 0.8)));
}

function intersect(a: Clip, b: Clip): Clip {
  const x = Math.max(a.x, b.x);
  const y = Math.max(a.y, b.y);
  return { x, y, w: Math.max(0, Math.min(a.x + a.w, b.x + b.w) - x), h: Math.max(0, Math.min(a.y + a.h, b.y + b.h) - y) };
}

function linkProgram(gl: WebGL2RenderingContext, vs: string, fs: string): WebGLProgram {
  const compile = (type: number, src: string) => {
    const s = gl.createShader(type)!;
    gl.shaderSource(s, src);
    gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(`shader: ${gl.getShaderInfoLog(s)}`);
    return s;
  };
  const p = gl.createProgram()!;
  gl.attachShader(p, compile(gl.VERTEX_SHADER, vs));
  gl.attachShader(p, compile(gl.FRAGMENT_SHADER, fs));
  gl.linkProgram(p);
  if (!gl.getProgramParameter(p, gl.LINK_STATUS)) throw new Error(`program: ${gl.getProgramInfoLog(p)}`);
  return p;
}
